// NOTE: 'vertical-ref' is the cycle 2 mode for the Securitization vertical reference.
// The design contract §6 uses 'reference' for a no-auth single-surface mode — that mode
// is preserved here. 'vertical-ref' differs: it requires Keycloak auth and exposes the
// full nav (Dashboard | Marketplace | Administration | OrgSwitcher | avatar).
// Contract mode-name rename ADR deferred to cycle 4+ per brief §2.5.
//
// ── Mode split (decision-2026-07-25-broker-mode-split, option C+B) ────────────
// Until 2026-07-25 'vertical-ref' meant BOTH the meta broker (hub.do.place) and a
// tenant broker (dev.mtcm.do.place): one artifact, one mode string, distinguished
// only by runtime config.json. The only gate on the operator provisioning surface
// was role-based, so the tenant deployment shipped provision / realm-create /
// decommission — adr-075 D11 violated as deployed.
//
// The mode is now the discriminator, structurally:
//   'vertical-ref'    → the META broker. Operator surfaces live here and ONLY here.
//   'vertical-tenant' → a TENANT broker. No provisioning route is registered at all.
// There is deliberately NO second `instance.role` field: one discriminator, and it is
// the one every surface already reads.
export type InstanceMode =
  | 'tier-0'
  | 'reference'
  | 'tier-1-hub'
  | 'vertical-ref'
  | 'vertical-tenant';

export interface TileConfig {
  id: string;
  title: string;
  description: string;
  mark: string;
  action: 'order' | 'launch' | 'manage';
  href?: string;
  vertical: string;
  status: 'public' | 'private' | 'coming-soon';
  tags: string[];
  /**
   * ADR-075 steps 3–4: rollout surface of the tile's app deployment.
   * phase 'uat' → tile carries the active UAT slot (visible per the
   * ADR-013 groups predicate — enforced server-side); phase 'published'
   * → deployment number + date stay visible after publish (D8).
   */
  deployment?: {
    phase: 'uat' | 'published';
    deployNo: number;
    deployDate: string;
    releaseTag?: string;
  };
  instances?: {
    name: string;
    slug: string;
    type: string;
    tier: string;
  }[];
  /**
   * brief-17-admin-catalogue-tile-actions: simplified 4-value rollup of the
   * richer convention-app-status-model (dev → committed → registered →
   * compliant → built → uat → published) for the meta-broker's own admin
   * catalogue tiles. Mapping: pre-promotion states -> 'dev'; validated +
   * allocating into a vertical (scenario-promotion-allocation) -> 'onboarding';
   * uat -> 'uat'; published -> 'live'. Only rendered when isAdminCatalog.
   */
  lifecycleStatus?: 'dev' | 'onboarding' | 'uat' | 'live';
}

export interface BrokerConfig {
  instance: {
    mode: InstanceMode;
    name: string;
    slug: string;
    /**
     * Broker commerce type (ADR-046 d2). Default: 'b2b'.
     * 'b2c': consumer cart surface enabled; per-tenant consumer realm auth.
     * 'b2b': consumer cart structurally absent (not hidden, absent).
     * Absent from config.json = 'b2b' (safe default).
     */
    broker_type?: 'b2b' | 'b2c';
    /**
     * The tenant this broker serves. REQUIRED in 'vertical-tenant' mode and MUST be
     * absent in 'vertical-ref' (meta) mode — the second, independent declaration of
     * which side of the split this deployment is on (see validateConfig).
     *
     * It is a display/labelling value only. The tenant a request is booked against is
     * ALWAYS derived server-side from the Keycloak token, never from this field and
     * never from a request body.
     */
    tenant?: string;
  };
  auth: {
    enabled: boolean;
    keycloak?: {
      url: string;
      realm: string;
      clientId: string;
    };
  };
  catalog: {
    tiles: TileConfig[];
  };
  /** WP-ACP-2 (Brief 12): runtime feature flags surfaced to the SPA. */
  features?: {
    /** When true, PromoteModal routes to the flag-gated promote-to-uat broker
     *  route (validate + allocate + register-app-uat, NO publish) instead of the
     *  legacy validate->publish->allocate promote. Default false/absent. */
    promoteUatGate?: boolean;
    /** WP-ACP-3 (Brief 12): when true, the App Ops surface shows the "Set public"
     *  business gate (tenant-admin). Under this flag UAT approval is the technical
     *  gate only; set-public is the separate business decision that flips the app
     *  to public. Default false/absent. */
    setPublicGate?: boolean;
  };
  brandTokens?: Partial<{
    brandAccent: string;
    brandSurfaceInverted: string;
    brandWordmarkColor: string;
  }>;
}

export const ALLOWED_BRAND_TOKENS: (keyof NonNullable<BrokerConfig['brandTokens']>)[] = [
  'brandAccent',
  'brandSurfaceInverted',
  'brandWordmarkColor',
];

export const VALID_MODES: InstanceMode[] = [
  'tier-0',
  'reference',
  'tier-1-hub',
  'vertical-ref',
  'vertical-tenant',
];

// ── Mode split helpers (decision-2026-07-25-broker-mode-split) ────────────────

/** The meta broker's mode. Operator/provisioning surfaces exist only here. */
export const META_MODE = 'vertical-ref' as const;

/** A tenant broker's mode. Structurally carries no provisioning capability. */
export const TENANT_MODE = 'vertical-tenant' as const;

/**
 * The meta broker is a singleton and owns exactly one Keycloak realm. This is the
 * anchor the mode/auth cross-check uses: a config claiming META_MODE while pointing
 * at some other realm is a mode/auth shape mismatch and is refused at startup, so a
 * copy-pasted config.json cannot silently resurrect the meta/tenant collision.
 */
export const META_BROKER_REALM = 'meta-broker';

export function isMetaBroker(mode: InstanceMode): boolean {
  return mode === META_MODE;
}

export function isTenantBroker(mode: InstanceMode): boolean {
  return mode === TENANT_MODE;
}

/**
 * Modes that are Keycloak-authenticated, broker-API-backed surfaces. Both sides of
 * the split are: the split is about which CAPABILITIES are registered, not about
 * whether the SPA authenticates or reads its catalogue from the API.
 */
export function isAuthenticatedBroker(mode: InstanceMode): boolean {
  return mode === META_MODE || mode === TENANT_MODE;
}
